import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	CommentAgentConversationsContext,
	useAgentSessionsContext,
} from './AgentSessionsContext.ts';
import { AgentSessionPollingContext } from './AgentSessionPollingProvider.tsx';
import { pollCommentAgentSessions } from './commentAgentSessionPolling.ts';
import { AgentSessionsProvider } from './AgentSessionsProvider.tsx';
import { usePendingCommentAgentSessionIds } from './optimisticCommentAgentSession.ts';
import {
	getSharedCommentAgentSessions,
	type SharedCommentAgentSessions,
} from './sharedCommentAgentSessions.ts';
import { useCommentAgentSessionState } from './useCommentAgentSession.ts';
import type { CommentAgentSession, SessionStateResult } from './useCommentAgentSession.ts';
import type { CommentAgentSessionMap } from './usePageCommentAgentSessions.ts';

export type CommentAgentSessionSnapshot = {
	commentId: string;
	/** A conversation is absent only during optimistic startup. */
	conversationId: string | null;
	state: CommentAgentSession['state'];
	agentId: string | null;
	agentName: string | null;
	avatarUrl: string | null;
};

type SessionSource = Omit<
	NonNullable<ReturnType<CommentAgentSessionMap['get']>>,
	'conversationId'
> & {
	conversationId: string | undefined;
	retryKey: string;
};
// An absent result means observation failed, without reporting an agent FAILED outcome.
type SessionResult = { source: SessionSource; result?: SessionStateResult };
type Props = {
	contentId: string;
	contentType?: string;
	/** Keep stable for the lifetime of this publisher. Cleanup clears the prior recipient. */
	onChange: (sessions: CommentAgentSessionSnapshot[]) => void;
	/** Live subscriptions stay mounted; only visible embedded consumers request polling. */
	pollingEnabled?: boolean;
};
type RowProps = {
	commentId: string;
	session: SessionSource;
	recordSessionResult: (commentId: string, row: SessionResult) => void;
};
const renderNothing = () => null;

function SessionSnapshotPublisher({
	commentId,
	session,
	recordSessionResult,
	pollingEnabled: demand,
	contentType,
}: RowProps & Pick<Props, 'pollingEnabled' | 'contentType'>) {
	const result = useCommentAgentSessionState(
		commentId,
		session.conversationId,
		session.state ?? undefined,
		contentType,
	);
	const { session: liveSession, pollingEnabled, onPollRequest } = result;
	const pollingScope = useContext(AgentSessionPollingContext);
	useEffect(() => {
		if (!demand || !pollingEnabled || !pollingScope) return;
		return pollCommentAgentSessions(pollingScope.environment, pollingScope.pageId, onPollRequest);
	}, [demand, pollingEnabled, pollingScope, onPollRequest]);
	const { state, conversationId, agentIdentity } = liveSession;
	const row = useMemo(
		() => ({
			source: session,
			result: { session: { state, conversationId, agentIdentity }, pollingEnabled, onPollRequest },
		}),
		[session, state, conversationId, agentIdentity, pollingEnabled, onPollRequest],
	);
	useEffect(() => recordSessionResult(commentId, row), [commentId, row, recordSessionResult]);
	return null;
}

function ContentSessionSnapshots({
	onChange,
	pollingEnabled,
	contentType,
	pendingCommentIds,
	store,
}: Props & { pendingCommentIds: readonly string[]; store: SharedCommentAgentSessions }) {
	const sessionMap = useAgentSessionsContext();
	const conversationMap = useContext(CommentAgentConversationsContext);
	const sources = useMemo(() => {
		// Include optimistic startup before the backend has associated the comment with a conversation.
		const sessionsByCommentId = new Map<string, Omit<SessionSource, 'retryKey'>>(sessionMap);
		for (const commentId of pendingCommentIds) {
			// Completed conversations may be absent from sessionMap; don't recreate them as pending.
			if (sessionsByCommentId.has(commentId) || conversationMap.has(commentId)) continue;
			sessionsByCommentId.set(commentId, {
				conversationId: undefined,
				state: null,
				agentId: null,
				agentName: null,
				avatarUrl: null,
			});
		}
		// A new conversation or agent identity permits a retry; a state refresh alone does not.
		return new Map(
			Array.from(sessionsByCommentId, ([commentId, session]) => [
				commentId,
				{
					...session,
					retryKey: JSON.stringify([
						commentId,
						session.conversationId,
						session.agentId,
						session.agentName,
						session.avatarUrl,
					]),
				},
			]),
		);
	}, [sessionMap, conversationMap, pendingCommentIds]);
	const sessionResults = useRef(new Map<string, SessionResult>());
	const lastPublished = useRef<{
		rows: SessionResult[];
		sessionMap: CommentAgentSessionMap;
		conversationMap: ReadonlyMap<string, string>;
	}>();

	const publishSnapshot = useCallback(() => {
		const rows: SessionResult[] = [];
		const sessions: CommentAgentSessionSnapshot[] = [];
		const states = new Map<string, SessionStateResult>();
		for (const [commentId, source] of sources) {
			const row = sessionResults.current.get(commentId);
			// Wait for current live results; failed rows stay unknown until their retry identity changes.
			if (!row) return;
			if (row.source !== source && (row.result || row.source.retryKey !== source.retryKey)) return;
			rows.push(row);
			if (!row.result) continue;
			states.set(commentId, row.result);
			const { state, agentIdentity } = row.result.session;
			if (state && (source.conversationId || state === 'THINKING')) {
				sessions.push({
					commentId,
					conversationId: source.conversationId ?? null,
					state,
					agentId: source.agentId ?? agentIdentity?.agentId ?? null,
					agentName: source.agentName ?? agentIdentity?.agentName ?? null,
					avatarUrl: source.avatarUrl ?? agentIdentity?.avatarUrl ?? null,
				});
			}
		}
		const previous = lastPublished.current;
		if (
			previous?.sessionMap === sessionMap &&
			previous.conversationMap === conversationMap &&
			previous.rows.length === rows.length &&
			previous.rows.every((row, index) => row === rows[index])
		)
			return;
		lastPublished.current = { rows, sessionMap, conversationMap };
		store.publish({ sessionMap, conversationMap, states });
		onChange(sessions);
	}, [sources, sessionMap, conversationMap, store, onChange]);

	const recordSessionResult = useCallback(
		(commentId: string, row: SessionResult) => {
			if (sessionResults.current.get(commentId) === row) return;
			sessionResults.current.set(commentId, row);
			publishSnapshot();
		},
		[publishSnapshot],
	);

	useEffect(() => {
		for (const id of sessionResults.current.keys()) {
			if (!sources.has(id)) sessionResults.current.delete(id);
		}
		publishSnapshot();
	}, [sources, publishSnapshot]);
	useEffect(
		() => () => {
			lastPublished.current = undefined;
			onChange([]);
		},
		[onChange],
	);

	return (
		<>
			{Array.from(sources, ([commentId, session]) => (
				<GenericErrorBoundary
					key={session.retryKey}
					attribution={Attribution.COMMENTS}
					renderOnError={renderNothing}
					onError={() => {
						// Record the missing result so healthy sessions can publish without waiting for this row.
						recordSessionResult(commentId, { source: session });
					}}
				>
					<SessionSnapshotPublisher
						commentId={commentId}
						session={session}
						recordSessionResult={recordSessionResult}
						pollingEnabled={pollingEnabled}
						contentType={contentType}
					/>
				</GenericErrorBoundary>
			))}
		</>
	);
}

function EnabledCommentAgentSessionPublisher(props: Props) {
	const environment = useRelayEnvironment();
	const { cloudId, userId } = useSessionData();
	const store = useMemo(
		() => getSharedCommentAgentSessions(environment, cloudId ?? '', userId ?? '', props.contentId),
		[environment, cloudId, userId, props.contentId],
	);
	const pendingCommentIds = usePendingCommentAgentSessionIds(props.contentId, userId ?? '');
	const [recoveryScope, setRecoveryScope] = useState<SharedCommentAgentSessions>();
	useEffect(() => {
		// Remember a save even if the optimistic indicator expires while discovery is suspended.
		setRecoveryScope((previous) => {
			if (pendingCommentIds.length > 0) return store;
			return previous === store ? previous : undefined;
		});
	}, [store, pendingCommentIds]);
	const scopeKey = useMemo(
		() => JSON.stringify([cloudId, userId, props.contentId]),
		[cloudId, userId, props.contentId],
	);
	useEffect(() => store.retainOwner(), [store]);
	return (
		<AgentSessionsProvider
			key={scopeKey}
			pageId={props.contentId}
			refetchOnMount={pendingCommentIds.length > 0 || recoveryScope === store}
		>
			<ContentSessionSnapshots {...props} pendingCommentIds={pendingCommentIds} store={store} />
		</AgentSessionsProvider>
	);
}

/** Publishes current comment session data for embedded content to consume in its own React UI. */
export function CommentAgentSessionPublisher({
	contentType = 'whiteboard',
	pollingEnabled = false,
	...props
}: Props): React.JSX.Element | null {
	if (!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) return null;
	return (
		<EnabledCommentAgentSessionPublisher
			{...props}
			contentType={contentType}
			pollingEnabled={pollingEnabled}
		/>
	);
}
